import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { Confirm, Container, Grid } from 'semantic-ui-react';
import toastr from 'toastr';
import * as appActions from '../../../../actions/appActions';
import * as allTaskViewActions from '../redux/actions';
import TaskForm from './TaskForm';
import './ManageTaskForm.css';
import { ASSET_TYPES, EVENT_SOURCE, STATUS_LIST } from '../../../../HelperMethods/enums';

import Comments from '../../comments/TaskComments';
import {
  ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS,
  PICKLIST_OPTIONS,
  findAffectingChild,
  INPUT_TYPE_FORMAT,
} from './lib/constants';
import * as manageTaskFormActions from './redux/actions';
import apiService from '../../../../service/apiService';
import logUtils from '../../../../HelperMethods/LogUtils';
import Joyn2Loader from '../../../common/Joyn2Loader';
import Modal from '../../../common/Modal';
import { getAccessOptionsForResourceAndUser } from '../../../../HelperMethods/UserUtilities';
import { getOptionIdfromNameForMetadataPicklists, inflateResponse } from '../../../../HelperMethods/UtilityFunctions';

import {
  ACCESS_OPTIONS,
  ENTITY_ACCESS_TYPE,
  RESOURCE_TYPE,
  RESOURCES,
} from '../../../../HelperMethods/role-resource-constants';
import { taskHistoryMapper } from './lib/Mapper';
import { ATTRIBUTE_TYPE, mapFormValuesToEntity, validateMandatoryAttribute } from '../../../../HelperMethods/FormUtils';
import FormHeader from '../../../common/FormHeader';
import * as manageApprovalActions from '../../../approvalGridView/redux/actions';
import { TASK_ACTION, WATCHER_GROUP } from '../../../../HelperMethods/constants';
import { CONTAINERS, resetView, closeView } from '../../../common/cardNavigationComponent/cardNavigationRedux';
import CardNavigationComponent from '../../../common/cardNavigationComponent/CardNavigationComponent';
import UserCard from '../../../common/userCard/UserCard';
import { getIsDRScoreEnabled } from '../../../../reducers/appReducer';
import { isAdhocFileUploadingSelector } from './redux/reducer';
import { loadAllPicklistMappings } from '../../../picklistAndPicklistMappingManagement/picklistMappingManagement/redux/actions';
import { loadWatcherGroups } from '../../../generalConfig/watcherGroupManagement/redux/actions';
import {
  loadPicklists,
  downloadAllAssetAttributes,
} from '../../../picklistAndPicklistMappingManagement/picklistManagement/redux/actions';
import {
  getPriorityTemplateSpecs,
  getPriorityList,
  isTaskParent,
  getAssetCriteriaRequestObj,
  computeWorkflowAndPreferredLocation,
  shouldCheckForAutoResolveFirstTask,
  getUrlQueryParams,
} from '../../../../HelperMethods/TaskUtilities';
import { findErrorsToScrollInToView } from './lib/helper';
import { actionRecordUtils } from '../../../../HelperMethods/LocalStorageUtils';

class ManageTaskForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.eventSourceOptions = Object.values(EVENT_SOURCE).map((option) => {
      return { key: option, value: option, text: option };
    });
    this.statusOptions = Object.values(STATUS_LIST).map((option) => ({
      key: option.key,
      value: option.key,
      text: option.text,
    }));

    this.ifSmallScreen = window.screen.width < 576;

    this.state = {
      isPageLoading: false,
      buttonDisableState: false,
      confirmModal: {
        content: '',
        header: '',
        onCancel: () => {},
        onConfirm: () => {},
        open: false,
        cancelButton: 'Cancel',
        confirmButton: 'Ok',
      },
    };

    this.taskFormRef = React.createRef();
    this.confirmDeleteModal = React.createRef();
    this.manifestForm = React.createRef();
    this.onFieldChange = this.onFieldChange.bind(this);
  }

  async componentDidMount() {
    const {
      match: {
        params: { taskId },
      },
      location,
      taskFormActions,
      loadPicklistMappingList,
      loadWatcherGroupsForField,
      fieldId,
      loadAllPicklists,
      loadAssetAttributes,
    } = this.props;
    this.setIsPageLoading(true);
    const promises = [loadPicklistMappingList(), loadWatcherGroupsForField(fieldId), loadAssetAttributes()];
    Promise.all(promises);
    if (taskId) {
      try {
        await taskFormActions.loadTaskForm({ component: this, taskId });
      } catch (e) {
        console.error(e);
      } finally {
        this.setIsPageLoading(false);
      }
    } else {
      loadAllPicklists();
      const queryParams = getUrlQueryParams(location);
      this.loadCreateTaskForm(queryParams);
      this.setIsPageLoading(false);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      fieldId: prevFieldId,
      location: prevLocation,
      selectedTaskTypeGroups: prevSelectedTaskTypeGroups,
    } = prevProps;
    const { fieldId, location, taskFormActions, selectedTaskTypeGroups, originalTask } = this.props;
    if (prevFieldId !== fieldId) {
      // field has been changed
      this.onFieldChange();
    }
    if (location !== prevLocation) {
      // eslint-disable-next-line no-unused-expressions
      this.taskFormRef.current && this.taskFormRef.current.scrollTo(0, 0);
      taskFormActions.resetTaskFormComponent();
      this.componentDidMount();
    }
    // Reload task type picklist if task type groups changed and we're creating a new task
    if (!_.isEqual(prevSelectedTaskTypeGroups, selectedTaskTypeGroups) && !originalTask?.id) {
      taskFormActions.loadTaskTypePicklist({ fieldId, isCreate: true, selectedTaskTypeGroups });
    }
  }

  componentWillUnmount() {
    const { taskFormActions, closeViewInNavigationComponent } = this.props;
    taskFormActions.resetTaskFormComponent();
    closeViewInNavigationComponent(CONTAINERS.TASK_VIEW);
  }

  loadCreateTaskForm = (params = {}) => {
    const { taskFormActions, fieldId, allTasks } = this.props;
    taskFormActions.loadCreateTaskForm(Object.assign(params, { fieldId }), allTasks);
  };

  cancelTask = () => {
    const {
      match: { url: baseUrl },
      requestPageView,
      taskFormActions,
      location,
    } = this.props;
    const taskViewPageCheck = baseUrl.split('/').includes('taskViewPage');
    const queryParams = getUrlQueryParams(location);
    taskFormActions.isTaskTypeParent(false);
    if (queryParams.parentLinkedTaskId) {
      this.props.history.goBack();
    } else if (taskViewPageCheck) {
      this.props.history.push('/taskViewPage');
    } else if (requestPageView) {
      this.props.history.push(`/requestViewPage${requestPageView}`);
    } else {
      this.props.history.goBack();
    }
  };

  getStatusForSelectedTaskType = (taskType) => {
    const { loggedInUser } = this.props;
    const accessOptions = getAccessOptionsForResourceAndUser({
      resource: RESOURCES.ENTITY.TASK,
      resourceType: RESOURCE_TYPE.ENTITY,
      accessOptionName: ACCESS_OPTIONS.STATUS.name,
      entityAccessType: ENTITY_ACCESS_TYPE.CREATE,
      user: loggedInUser,
    });
    if (accessOptions.includes(ACCESS_OPTIONS.STATUS.options.ALL_STATUS)) {
      return taskType && taskType.needAcceptance && !taskType.isParent
        ? STATUS_LIST.NOT_READY.key
        : STATUS_LIST.OPEN.key;
    }
    if (accessOptions.includes(ACCESS_OPTIONS.STATUS.options.DRAFT_STATUS)) {
      return STATUS_LIST.DRAFT.key;
    }
    console.warn('invalid access options of status for create task');
    return STATUS_LIST.OPEN.key;
  };

  onTaskTypeChange = ({ value: taskTypeId, formik }) => {
    const {
      originalTask,
      taskTypeOptions,
      taskFormActions,
      assigneeOptions,
      assetOptions,
      stopOptions,
      customPicklistOptions,
    } = this.props;
    if (!taskTypeId) {
      taskFormActions.clearManifestForm();
      taskFormActions.clearParentTaskTemplate();
      if (this.props.bulkCreateTasksView && !this.props.originalTask.id) {
        taskFormActions.clearCriteriaTemplate();
      }
      return;
    }
    const taskType = taskTypeOptions.items.find((option) => option.value === taskTypeId)?.originalobject;

    if (taskType.workflowVersions || taskType.isDynamicParentChild) {
      taskFormActions.isTaskTypeParent(true);
    } else {
      taskFormActions.isTaskTypeParent(false);
    }
    const { basePriority, maxPriority, timeToNextPriority } = getPriorityTemplateSpecs(
      _.get(taskType, 'priorityTemplate')
    );
    const priorityList = getPriorityList(basePriority, maxPriority, _.get(taskType, 'priorityAlias'));

    if (originalTask?.id) {
      const ifPriorityBelongsToTheList = priorityList.find((priority) => priority.value === formik?.values?.s_priority);
      const reqPriority = ifPriorityBelongsToTheList ? formik?.values?.s_priority : _.get(priorityList, `[${0}].value`);
      formik.setFieldValue('s_priority', reqPriority, false);
      formik.setFieldValue('parts_required', [{ picklistId: '', picklistOptionId: '', quantity: 0 }], false);
      formik.setFieldValue('schedule_duration', `${taskType.duration}`, false);
    } else if (!originalTask?.id) {
      formik.setFieldValue('schedule_duration', `${taskType.duration}`, false);
      formik.setFieldValue('s_stop', '', false);
      formik.setFieldValue('s_asset', '', false);
      formik.setFieldValue('s_assigneeid', [], false);
      formik.setFieldValue('notification_watchers', [], false);
      formik.setFieldValue('parts_required', [{ picklistId: '', picklistOptionId: '', quantity: 0 }], false);
      formik.setFieldValue('s_priority', _.get(priorityList, `[${0}].value`), false);
      formik.setFieldValue('s_timetonextpriority', timeToNextPriority, false);
    }
    formik.setFieldValue('s_status', this.getStatusForSelectedTaskType(taskType), false);
    formik.setFieldValue('s_level1', '', false);
    formik.setFieldValue('s_level2', '', false);
    // check if current priority is between base and max priority
    taskFormActions.loadSchemaForTaskType(
      taskType,
      formik,
      assigneeOptions,
      assetOptions,
      taskTypeOptions,
      customPicklistOptions,
      stopOptions,
      originalTask
    );
    // loading template for parent tasks while they are getting created.
    if ((taskType.isParent || taskType.isDynamicParentChild) && !this.props.originalTask.id) {
      taskFormActions.loadParentTaskTemplate(taskType);
    } else {
      taskFormActions.clearParentTaskTemplate();
    }
    if (this.props.bulkCreateTasksView && !this.props.originalTask.id) {
      taskFormActions.loadAssetCriteriaTemplate(taskType);
    }
  };

  [ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS] = ({ value: taskTypeId, formik }) => {
    const {
      assetOptions,
      stopOptions,
      customPicklistOptions,
      assigneeOptions,
      taskTypeOptions,
      taskFormActions,
      fieldId,
      originalTask,
    } = this.props;
    if (!taskTypeId) {
      taskFormActions.clearManifestForm();
      return;
    }
    const assetId = formik.values.s_asset || formik.values.s_stop;
    const asset = [..._.get(assetOptions, 'items', []), ..._.get(stopOptions, 'items', [])].find(
      (option) => option.value === assetId
    ).originalobject;
    const selectedTaskType = taskTypeOptions.items.find((option) => option.value === taskTypeId).originalobject;
    formik.setFieldValue('schedule_duration', `${selectedTaskType.duration}`, false);
    formik.setFieldValue('s_assigneeid', [], false);
    formik.setFieldValue('s_status', this.getStatusForSelectedTaskType(selectedTaskType), false);
    formik.setFieldValue('parts_required', [{ picklistId: '', picklistOptionId: '', quantity: 0 }], false);
    const { basePriority, maxPriority, timeToNextPriority } = getPriorityTemplateSpecs(
      _.get(selectedTaskType, 'priorityTemplate')
    );
    const priorityList = getPriorityList(basePriority, maxPriority, _.get(selectedTaskType, 'priorityAlias'));

    formik.setFieldValue('s_priority', _.get(priorityList, `[${0}].value`), false);
    formik.setFieldValue('s_timetonextpriority', timeToNextPriority, false);
    formik.setFieldValue('s_level1', '', false);
    formik.setFieldValue('s_level2', '', false);
    taskFormActions.loadAssigneePicklist(asset, selectedTaskType, fieldId);
    taskFormActions.loadSchemaForTaskType(
      selectedTaskType,
      formik,
      assigneeOptions,
      assetOptions,
      taskTypeOptions,
      customPicklistOptions,
      stopOptions,
      originalTask
    );
    taskFormActions.loadWatchersPicklist(asset);
  };

  onCustomPicklistValueChange = ({ attributeObj, formik, name, value }) => {
    const newFormValues = { ...formik.values, [name]: value };
    if (attributeObj.type === ATTRIBUTE_TYPE.PICKLIST || attributeObj.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST) {
      this.resetChildPicklistsForParentPicklistAttributeName({ attributeObj, formik, name, value, newFormValues });
    }
  };

  setScoreFromDR = (formVales, formik) => {
    const { isDRScoreEnabled } = this.props;
    if (formVales.s_onlyAllowManualScoreUpdate || !isDRScoreEnabled) return;
    if (!this.fetchScoreDebouncedFn) {
      this.fetchScoreDebouncedFn = _.debounce(async (formValues) => {
        const {
          schema,
          originalTask,
          assigneeOptions,
          taskTypeOptions,
          stopOptions,
          assetOptions,
          notificationWatchers,
          customPicklistOptions,
          fieldId,
          loggedInUser,
        } = this.props;
        const picklistOptions = {
          assigneeOptions,
          taskTypeOptions,
          stopOptions,
          assetOptions,
          notificationWatchers,
          customPicklistOptions,
        };
        const setValueParams = {
          task: originalTask,
          fieldId,
          loggedInUser,
          ...picklistOptions,
        };
        const task = mapFormValuesToEntity(formValues, schema, setValueParams);
        if (task.taskTypeId && task.assetId) {
          try {
            const { priority, timeToNextPriority } = await this.getTaskScoreAndIncrementForCurrentTaskData(task);
            if (!_.isNil(priority) && !_.isEmpty(priority)) {
              formik.setFieldValue('s_priority', `${priority}`, false);
            }
            if (!_.isNil(timeToNextPriority)) {
              formik.setFieldValue('s_timetonextpriority', `${timeToNextPriority}`, false);
            }
          } catch (e) {
            console.error(e);
          }
        }
      }, 1000);
    }
    this.fetchScoreDebouncedFn(formVales);
  };

  updateWatcherGroupsUsingConfig = ({ formik, attributeObj = {}, value }) => {
    const { taskTypeOptions, picklistMappings, allPicklistData, watchergroups } = this.props;

    const selectedTaskTypeId = formik.values.s_tasktype;
    const { watcherGroupConfig } = taskTypeOptions.items.find(
      (option) => option.value === selectedTaskTypeId
    ).originalobject;

    const { id: watcherGroupPicklistId } = allPicklistData.find((p) => p.name === WATCHER_GROUP) || {};
    const { picklistId } = attributeObj;

    if (
      watcherGroupPicklistId &&
      picklistId &&
      watcherGroupConfig &&
      watcherGroupConfig.entityId === attributeObj.id &&
      watcherGroupConfig.entityPicklistWatcherGroupMappingId &&
      value &&
      typeof value === 'string'
    ) {
      const picklistMapping = picklistMappings.find(
        (p) => p.id === watcherGroupConfig.entityPicklistWatcherGroupMappingId
      );
      if (picklistMapping) {
        const watcherGropuPicklistIndex = (picklistMapping.picklistIds || []).indexOf(watcherGroupPicklistId);
        if (watcherGropuPicklistIndex > -1) {
          const watcherGroupsOptionNameMap = _.keyBy(watchergroups, (item) =>
            getOptionIdfromNameForMetadataPicklists(item.name)
          );

          const watcherGroupsToSelect = (picklistMapping.mappingTable || [])
            .filter((mapping) => mapping.indexOf(value) > -1)
            .map((mapping) => mapping[watcherGropuPicklistIndex]);
          if (formik) {
            formik.setFieldValue(
              'watcher_groups',
              watcherGroupsToSelect.map((item) => watcherGroupsOptionNameMap[item]?.id).filter((item) => item)
            );
          }
        }
      }
    }
  };

  onGlobalAttributeValueChange = ({ attributeObj, formik, name, value }) => {
    const newFormValues = { ...formik.values, [name]: value };
    if (attributeObj.type === ATTRIBUTE_TYPE.PICKLIST || attributeObj.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST) {
      this.resetChildPicklistsForParentPicklistAttributeName({ attributeObj, formik, name, value, newFormValues });
    }

    this.updateWatcherGroupsUsingConfig({
      attributeObj,
      value,
      formik,
    });

    // this.setScoreFromDR(newFormValues, formik);
  };

  onScoreOrIncrementChange = ({ formik }) => {
    formik.setFieldValue('s_onlyAllowManualScoreUpdate', true);
  };

  resetChildPicklistsForParentPicklistAttributeName = ({ attributeObj, formik, value, newFormValues }) => {
    const { schema, customPicklistOptions, taskTypeOptions, assetOptions, stopOptions, assigneeOptions } = this.props;
    findAffectingChild({
      attributes: schema,
      entityVal: value,
      entityType: attributeObj.id,
      formik,
      formValues: _.cloneDeep(newFormValues),
      customPicklistOptions,
      taskTypeOptions,
      assetOptions,
      assigneeOptions,
      stopOptions,
    });
  };

  checkValidAssigneesAndWatchers = ({ assignees = [], formik, watchers = [] }) => {
    const assigneesFound = assignees.filter(({ id }) => (formik?.values?.s_assigneeid ?? []).includes(id));
    const filteredAssigneeIds = (assigneesFound || []).map((val) => val.id);
    const watchersFound = watchers.filter(({ id }) => (formik?.values?.notification_watchers ?? []).includes(id));
    const filteredWatcherIds = (watchersFound || []).map((val) => val.id);
    return { filteredAssigneeIds, filteredWatcherIds };
  };

  onStopChange = async ({ name, value: stopId, formik }) => {
    const {
      taskFormActions,
      fieldId,
      stopOptions,
      taskTypeOptions,
      assetOptions,
      schema,
      customPicklistOptions,
      assigneeOptions,
      originalTask,
      parentTaskTemplate,
    } = this.props;
    formik.setFieldValue('s_asset', '', false);
    taskFormActions.clearPicklist(PICKLIST_OPTIONS.ASSET);
    if (!stopId) {
      taskFormActions.clearStop();
      if (!originalTask.id && _.size(_.get(parentTaskTemplate, 'items'))) {
        taskFormActions.loadPreferredLocationsAndAssigneeForParentTaskTemplateItem([]);
      }
      return;
    }
    // eslint-disable-next-line
    const stop = stopOptions.items.find((option) => option.value === stopId).originalobject;
    const selectedTaskTypeId = formik.values.s_tasktype;
    const selectedTaskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId).originalobject;

    taskFormActions.loadAssetPicklist({
      taskType: selectedTaskType,
      searchString: '',
      stopId,
      filterAssetLevels: selectedTaskType.allowAssetLevelsInfo,
    });
    const promises = [
      taskFormActions.loadAssigneePicklist(stop, selectedTaskType, fieldId),
      taskFormActions.loadWatchersPicklist(stop),
    ];
    const [assignees, watchers] = await Promise.all(promises);
    if (!originalTask.id && _.size(_.get(parentTaskTemplate, 'items'))) {
      const preferredLocationData = computeWorkflowAndPreferredLocation(
        selectedTaskType,
        stop,
        this.props.parentTaskTemplate
      );
      taskFormActions.loadPreferredLocationsAndAssigneeForParentTaskTemplateItem(preferredLocationData);
    }
    if (!originalTask.id) {
      formik.setFieldValue('s_assigneeid', [], false);
      formik.setFieldValue('notification_watchers', [], false);
    } else {
      const { filteredAssigneeIds, filteredWatcherIds } = this.checkValidAssigneesAndWatchers({
        formik,
        assignees,
        watchers,
      });
      formik.setFieldValue('s_assigneeid', [...filteredAssigneeIds, ''], false);
      formik.setFieldValue('notification_watchers', [...filteredWatcherIds], false);
    }
    formik.setFieldValue('s_assetLevelsInfo', {}, false);
    const newFormValues = { ...formik.values, [name]: stopId };
    findAffectingChild({
      attributes: schema,
      entityVal: stop,
      entityType: 'asset',
      formik,
      formValues: _.cloneDeep(newFormValues),
      customPicklistOptions,
      taskTypeOptions,
      assetOptions,
      assigneeOptions,
      stopOptions,
    });
    this.setScoreFromDR(newFormValues, formik);
  };

  onStopSelected = async (formik, asset) => {
    const { taskFormActions } = this.props;
    if (!formik.values.s_stop) {
      const stop = await taskFormActions.getStop(asset.stopId);
      if (stop) {
        formik.setFieldValue('s_stop', stop.id, false);
      }
    } else {
      taskFormActions.clearStop();
    }
  };

  onAssetChange = async ({ name, value: assetId, formik }) => {
    const {
      taskFormActions,
      fieldId,
      assetOptions,
      taskTypeOptions,
      schema,
      customPicklistOptions,
      assigneeOptions,
      stopOptions,
      originalTask,
      parentTaskTemplate,
    } = this.props;

    const selectedTaskTypeId = formik.values.s_tasktype;
    const selectedTaskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId).originalobject;
    if (!assetId) {
      if (_.get(formik, 'values.s_stop') && !originalTask.id && _.size(_.get(parentTaskTemplate, 'items'))) {
        const stop = stopOptions?.items?.find(
          (option) => option.value === _.get(formik, 'values.s_stop')
        )?.originalobject;

        const preferredLocationData = computeWorkflowAndPreferredLocation(
          selectedTaskType,
          stop,
          this.props.parentTaskTemplate
        );
        taskFormActions.loadPreferredLocationsAndAssigneeForParentTaskTemplateItem(preferredLocationData);
      }
      return;
    }
    // eslint-disable-next-line
    const asset = assetOptions.items.find((option) => option.value === assetId).originalobject;
    taskFormActions.loadAssetLevelsPicklist(asset.id);
    formik.setFieldValue('s_assetLevelsInfo', {}, false);
    const promises = [
      taskFormActions.loadAssigneePicklist(asset, selectedTaskType, fieldId),
      taskFormActions.loadWatchersPicklist(asset),
    ];
    const [assignees, watchers] = await Promise.all(promises);
    if (!_.get(formik, 'values.s_stop')) {
      this.onStopSelected(formik, asset);
    }
    if (!originalTask.id && _.size(_.get(parentTaskTemplate, 'items'))) {
      const preferredLocationData = computeWorkflowAndPreferredLocation(
        selectedTaskType,
        asset,
        this.props.parentTaskTemplate
      );
      taskFormActions.loadPreferredLocationsAndAssigneeForParentTaskTemplateItem(preferredLocationData);
    }
    if (!originalTask.id) {
      formik.setFieldValue('s_assigneeid', [], false);
      formik.setFieldValue('notification_watchers', [], false);
    } else {
      const { filteredAssigneeIds, filteredWatcherIds } = this.checkValidAssigneesAndWatchers({
        formik,
        assignees,
        watchers,
      });
      formik.setFieldValue('s_assigneeid', [...filteredAssigneeIds, ''], false);
      formik.setFieldValue('notification_watchers', [...filteredWatcherIds], false);
    }
    formik.setFieldValue('s_level1', '', false);
    formik.setFieldValue('s_level2', '', false);
    const stop = stopOptions?.items?.find?.((option) => option.value === _.get(formik, 'values.s_stop')).originalobject;
    const newFormValues = { ...formik.values, [name]: assetId };
    findAffectingChild({
      attributes: schema,
      entityVal: stop,
      entityType: 'asset',
      formik,
      formValues: _.cloneDeep(newFormValues),
      customPicklistOptions,
      taskTypeOptions,
      assetOptions,
      assigneeOptions,
      stopOptions,
    });
    this.setScoreFromDR(newFormValues, formik);
  };

  onAssigneeChange = async ({ name, value, formik }) => {
    const { assetOptions, taskTypeOptions, schema, customPicklistOptions, assigneeOptions, stopOptions } = this.props;
    if (!_.size(value)) {
      return;
    }
    // eslint-disable-next-line
    const assignees = assigneeOptions.items
      .filter((option) => value.includes(option.value))
      .map((val) => val.originalobject);
    formik.setFieldValue('s_assigneeid', value, false);
    formik.setFieldTouched('s_assigneeid', true, false);
    const selectedTaskTypeId = _.get(formik, 'values.s_tasktype', '');
    const taskType = (taskTypeOptions?.items || []).find(
      (option) => option.value === selectedTaskTypeId
    )?.originalobject;
    if (taskType?.generateMultiple) {
      if ((value || []).filter((id) => id).length) {
        formik.setFieldValue('s_copyGenerateMultipleAssignee', true, false);
      } else {
        formik.setFieldValue('s_copyGenerateMultipleAssignee', false, false);
      }
    }
    const newFormValues = { ...formik.values, [name]: value };
    findAffectingChild({
      attributes: schema,
      entityVal: assignees,
      entityType: 'assignee',
      formik,
      formValues: _.cloneDeep(newFormValues),
      customPicklistOptions,
      taskTypeOptions,
      assetOptions,
      assigneeOptions,
      stopOptions,
    });
  };

  getTaskScoreAndIncrementForCurrentTaskData = async (task) => {
    const response = await apiService.getTaskScoreForTask(task);
    return response;
  };

  validateForm = (formValues) => {
    const { schema, originalTask, taskTypeOptions, bulkCreateTasksView, parentTaskTemplate } = this.props;
    const errorObject = Object.entries(schema).reduce((errors, [attributeName, attributeObject]) => {
      if (
        attributeObject.isMandatory ||
        (attributeObject.requiredOnResolve && formValues.s_status === STATUS_LIST.RESOLVED.key)
      ) {
        const validationObj = validateMandatoryAttribute(attributeObject, formValues[attributeName]);
        if (!validationObj.validation) {
          errors[attributeName] = validationObj.message;
          return errors;
        }
      }

      if (attributeObject.validations && typeof attributeObject.validations === 'function') {
        const validation = attributeObject.validations({ formValues, task: originalTask });
        if (!validation.isValid) errors[attributeName] = validation.error;
      } else if (attributeObject.validations && attributeObject.validations.length > 0) {
        for (const validation of attributeObject.validations) {
          if (!validation.validateFunc({ formValues, task: originalTask, taskTypeOptions, bulkCreateTasksView })) {
            errors[attributeName] = validation.message;
            break;
          }
        }
      }
      return errors;
    }, {});
    const selectedTaskTypeId = formValues.s_tasktype;
    const taskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId)?.originalobject;
    if (taskType?.isDynamicParentChild) {
      const validItems = (parentTaskTemplate?.items ?? []).filter((item) => item?.originalItem?.taskTypeId);
      if (validItems.length === 0) {
        errorObject.parentTaskTemplate = 'At least one child task should be there';
      }
    }
    return errorObject;
  };

  onFieldChange = () => {
    const { history } = this.props;
    history.goBack();
  };

  updateManifestFormData = async (taskId, formCreatedOnTaskCreation) => {
    if (this.manifestForm && this.manifestForm.current) {
      const formValuesAndUpdatedSignatures = this.manifestForm.current.getFormValuesAndUpdatedSignatures();
      let { updatedSignatures } = formValuesAndUpdatedSignatures;
      let { data: updatedFormData } = formValuesAndUpdatedSignatures;
      if (!updatedFormData.id) {
        updatedFormData.id = taskId;
      }
      updatedFormData.formCreatedOnTaskCreation = formCreatedOnTaskCreation;
      const actionRecord = actionRecordUtils.GET_ACTION_RECORD();
      if (actionRecord) {
        updatedFormData = { ...updatedFormData, modifiedBy: actionRecord.assigneeUserName, ignoreModifiedBy: true };
      }
      await apiService.upsertForm(updatedFormData);
      if (updatedSignatures && updatedSignatures.length) {
        updatedSignatures = updatedSignatures.map((signatureObject) => {
          let updatedSignatureObject = {
            id: updatedFormData.id,
            data: updatedFormData.data,
            signatureObject,
          };
          if (actionRecord) {
            updatedSignatureObject = {
              ...updatedFormData,
              modifiedBy: actionRecord.assigneeUserName,
              ignoreModifiedBy: true,
            };
          }
          return updatedSignatureObject;
        });
        await Promise.all(updatedSignatures.map((signature) => apiService.updateSignature(signature)));
      }
    }
  };

  /**
   * this function validates task form, manifest form and if attachment is uploading
   * @returns {boolean}
   */
  validateOverallTaskForm = async (
    formik,
    { validateEveryFieldOfManifestForm: validateEveryField, isTaskResolved, isCreate } = {
      validateEveryFieldOfManifestForm: false,
      isTaskResolved: false,
      isCreate: false,
    },
    values
  ) => {
    const validationResponse = await formik.validateForm();
    // validation of form
    let isManifestFormValid = true;
    let firstSectionSoftValidation = true;
    let invalidSectionLabel = '';
    if (this.manifestForm && this.manifestForm.current) {
      const {
        isValid,
        isFirstSectionValid,
        invalidSectionName = '',
      } = await this.manifestForm.current.validateFormData({
        validateEveryField,
        isTaskResolved,
        isCreate,
      });
      isManifestFormValid = isValid;
      firstSectionSoftValidation = isFirstSectionValid;
      invalidSectionLabel = invalidSectionName;
    }
    const { isAdhocFileUploading } = this.props;
    if (isAdhocFileUploading) {
      toastr.warning('Please wait while attachment is being uploaded');
      return false;
    }
    const isValid = _.isEmpty(validationResponse) && isManifestFormValid && !isAdhocFileUploading;
    if (isManifestFormValid && _.isEmpty(validationResponse) && !firstSectionSoftValidation && !isAdhocFileUploading) {
      this.setConfirmModalForValidationForFirstTask(values, formik, this.createTaskFunction, invalidSectionLabel);
      return false;
    }
    if (!isValid) {
      findErrorsToScrollInToView(validationResponse, isManifestFormValid);
      toastr.options.preventDuplicates = true;
      toastr.error('Please correct the highlighted validation errors prior to proceeding.');
    }
    return isValid;
  };

  validateReading = () => {
    const { originalTask: task } = this.props;
    if (task?.taskType?.rt_id && task?.asset?.type !== 'stop') {
      if (!task?.readingId) {
        toastr.error('Reading is Required for validation');
        return true;
      }
    }
    return false;
  };

  closeConfirmModal = () => {
    this.setState((prevState) => {
      return {
        confirmModal: {
          ...prevState.confirmModal,
          ...{
            open: false,
            cancelButton: 'Cancel',
            confirmButton: 'Ok',
          },
        },
      };
    });
  };

  setConfirmModalForValidationForFirstTask = (values, formik, createOrUpdateFunc, invalidSectionLabel) => {
    this.setState((prevState) => {
      return {
        confirmModal: {
          ...prevState.confirmModal,
          ...{
            open: true,
            header: 'Warning',
            content: `${invalidSectionLabel} is invalid. Do You Want To Proceed?`,
            onCancel: () => {
              const element = document.getElementById('manifestFormSegment');
              element.scrollIntoView();
              this.closeConfirmModal();
            },
            onConfirm: async () => {
              this.closeConfirmModal();
              const todaysDateMoment = moment(moment().format(INPUT_TYPE_FORMAT.date));
              const taskDateMoment = moment(_.get(values, 'schedule_duedate'), INPUT_TYPE_FORMAT.date);
              if (todaysDateMoment.diff(taskDateMoment, 'days') > 0) {
                this.setConfirmModal(values, formik, createOrUpdateFunc);
              } else {
                const res = await createOrUpdateFunc(values, formik);
                if (res) {
                  this.props.history.push('/taskViewPage');
                }
              }
            },
            cancelButton: 'No',
            confirmButton: 'Yes',
          },
        },
      };
    });
  };

  setConfirmModal = (values, formik, createOrUpdateFunc) => {
    const { history } = this.props;
    this.setState((prevState) => {
      return {
        confirmModal: {
          ...prevState.confirmModal,
          ...{
            open: true,
            header: 'Warning',
            content: 'You have selected a date in the past. Are you sure you want to continue?',
            onCancel: () => {
              const element = document.getElementById('scheduleSegment');
              element.scrollIntoView();
              this.closeConfirmModal();
            },
            onConfirm: async () => {
              this.closeConfirmModal();
              const res = await createOrUpdateFunc(values, formik);
              if (res) {
                history.push('/taskViewPage');
              }
            },
          },
        },
      };
    });
  };

  validateForPastDateAndPerformAction = (values, formik, createOrUpdateFunc) => {
    const todaysDateMoment = moment(moment().format(INPUT_TYPE_FORMAT.date));
    const taskDateMoment = moment(_.get(values, 'schedule_duedate'), INPUT_TYPE_FORMAT.date);
    if (todaysDateMoment.diff(taskDateMoment, 'days') > 0) {
      this.setConfirmModal(values, formik, createOrUpdateFunc);
      return null;
    }
    return createOrUpdateFunc(values, formik);
  };

  createTaskFunction = async (values, formik) => {
    this.setState({
      buttonDisableState: true,
    });
    const {
      taskViewActions,
      schema,
      originalTask,
      loggedInUser,
      assigneeOptions,
      taskTypeOptions,
      assetOptions,
      stopOptions,
      fieldId,
      tenantId,
      tasksEditMode,
      parentLinkedTask,
      // history,
      notificationWatchers,
      stopForSelectedAsset,
      customPicklistOptions,
      approvalActions,
      parentTaskTemplate,
      taskFormActions,
      bulkCreateTasksView,
    } = this.props;

    try {
      let taskRes;
      const taskId = uuid();

      const taskParams = {
        originalTask: { ...originalTask, id: taskId },
        picklistOptions: {
          assigneeOptions,
          taskTypeOptions,
          stopOptions,
          assetOptions,
          notificationWatchers,
          customPicklistOptions,
        },
        loggedInUser,
        fieldId,
        tenantId,
        tasksEditMode,
        parentLinkedTask,
        stopForSelectedAsset,
        taskActionBeingDone: TASK_ACTION.CREATE,
        parentTaskTemplate,
        bulkCreateTasksView,
      };
      taskFormActions.isTaskTypeParent(false);

      const selectedTaskTypeId = _.get(formik, 'values.s_tasktype', '');
      const taskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId)?.originalobject;
      await this.updateManifestFormData(taskId, true);
      if (_.size(taskType.approvalMatrix)) {
        // create workrequest
        taskRes = await approvalActions.upsertWorkRequest(values, schema, taskParams);
      } else {
        taskRes = await taskViewActions.createTask(values, schema, taskParams);
      }
      return taskRes;
    } catch (e) {
      logUtils.logError(e, 'Creating Task API Failed');
      throw e;
    } finally {
      this.setState({
        buttonDisableState: false,
      });
    }
  };

  getAssetsForAssetCriteriaWrapper = async (info) => {
    const inputObj = getAssetCriteriaRequestObj(info);
    const assetResponse = await apiService.getAssetsByAssetsCriteria(inputObj);
    const assets = inflateResponse(assetResponse);
    return assets;
  };

  onCreateButtonClicked = async (values, formik) => {
    const { assetCriteriaTemplate, taskViewActions, taskTypeOptions } = this.props;
    const { info = {} } = assetCriteriaTemplate;

    const isTaskFormValid = await this.validateOverallTaskForm(
      formik,
      {
        validateEveryFieldOfManifestForm: values.s_status === STATUS_LIST.RESOLVED.key,
        isTaskResolved: values.s_status === STATUS_LIST.RESOLVED.key,
        isCreate: true,
      },
      values
    );
    if (isTaskFormValid) {
      if (this.props.bulkCreateTasksView) {
        taskViewActions.startPageLoadingTask();
        const assetsForBulkTasks = await this.getAssetsForAssetCriteriaWrapper(info);
        taskViewActions.stopPageLoadingTask();
        if (!assetsForBulkTasks || !assetsForBulkTasks?.length) {
          toastr.error('No Assets to match criteria.');
          return null;
        }
        toastr.success(
          `Found ${assetsForBulkTasks.length} Assets Matching Criteria. Creating ${assetsForBulkTasks.length} Tasks.`
        );
      }

      const selectedTaskTypeId = _.get(values, 's_tasktype', '');
      const taskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId)?.originalobject;
      const { autoResolveFirstTaskValidation, sectionToValidate } = shouldCheckForAutoResolveFirstTask({
        tt: taskType,
      });
      return this.validateForPastDateAndPerformAction(
        {
          ...values,
          firstTaskValid: sectionToValidate
            ? sectionToValidate && autoResolveFirstTaskValidation
            : autoResolveFirstTaskValidation,
        },
        formik,
        this.createTaskFunction
      );
    }
    return null;
  };

  updateTaskFunction = async (values, formik) => {
    this.setState({
      buttonDisableState: true,
    });
    const {
      taskViewActions,
      schema,
      originalTask,
      loggedInUser,
      assigneeOptions,
      taskTypeOptions,
      assetOptions,
      stopOptions,
      fieldId,
      tenantId,
      tasksEditMode,
      history,
      notificationWatchers,
      stopForSelectedAsset,
      customPicklistOptions,
      approvalActions,
      taskFormActions,
      parentTaskTemplate,
      childTasksDictByWorkflowId,
    } = this.props;
    if (originalTask.isVirtual) {
      this.onCreateButtonClicked(values, formik);
      return;
    }

    const taskParams = {
      originalTask,
      picklistOptions: {
        assigneeOptions,
        taskTypeOptions,
        stopOptions,
        assetOptions,
        notificationWatchers,
        customPicklistOptions,
      },
      loggedInUser,
      fieldId,
      tenantId,
      tasksEditMode,
      stopForSelectedAsset,
      parentTaskTemplate,
      childTasksDictByWorkflowId,
    };

    try {
      let taskRes;
      taskFormActions.isTaskTypeParent(false);
      const selectedTaskTypeId = _.get(formik, 'values.s_tasktype', '');
      const taskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId)?.originalobject;

      await this.updateManifestFormData(originalTask.id, false);
      if (
        _.size(taskType.approvalMatrix) &&
        _.get(history, `location.state.from`, '').indexOf('workrequestlist') > -1
      ) {
        // update workrequest
        taskRes = await approvalActions.upsertWorkRequest(values, schema, taskParams);
      } else if (
        _.size(taskType.approvalMatrix) &&
        _.get(history, `location.state.from`, '').indexOf('approvalrequestlist') > -1
      ) {
        // update approvalrequestlist
        taskRes = await approvalActions.upsertApprovalRequest(values, schema, taskParams);
      } else {
        taskRes = await taskViewActions.updateTask(values, schema, taskParams);
      }
      // eslint-disable-next-line consistent-return
      return taskRes;
    } catch (e) {
      logUtils.logError(e, 'Updating Task API Failed');
      throw e;
    } finally {
      this.setState({
        buttonDisableState: false,
      });
    }
  };

  onUpdateButtonClicked = async (values, formik) => {
    const isTaskFormValid = await this.validateOverallTaskForm(
      formik,
      {
        validateEveryFieldOfManifestForm: values.s_status === STATUS_LIST.RESOLVED.key,
        isTaskResolved: values.s_status === STATUS_LIST.RESOLVED.key,
        isCreate: false,
      },
      values
    );
    let readingValidationFailed = false;
    if (values.s_status === STATUS_LIST.RESOLVED.key) {
      readingValidationFailed = this.validateReading();
    }
    if (isTaskFormValid && !readingValidationFailed) {
      return this.validateForPastDateAndPerformAction(values, formik, this.updateTaskFunction);
    }
    // eslint-disable-next-line consistent-return
    return null;
  };

  onResolveAndUpdateButtonClicked = async (values, formik) => {
    this.setState({
      buttonDisableState: true,
    });
    const isTaskFormValid = await this.validateOverallTaskForm(
      formik,
      {
        validateEveryFieldOfManifestForm: true,
        isTaskResolved: values.s_status === STATUS_LIST.RESOLVED.key,
        isCreate: true,
      },
      values
    );
    const readingValidationFailed = this.validateReading();
    if (isTaskFormValid && !readingValidationFailed) {
      const {
        taskViewActions,
        schema,
        originalTask,
        loggedInUser,
        assigneeOptions,
        taskTypeOptions,
        assetOptions,
        stopOptions,
        fieldId,
        tenantId,
        tasksEditMode,
        notificationWatchers,
        stopForSelectedAsset,
        customPicklistOptions,
        taskFormActions,
      } = this.props;

      const taskParams = {
        originalTask,
        picklistOptions: {
          assigneeOptions,
          taskTypeOptions,
          stopOptions,
          assetOptions,
          notificationWatchers,
          customPicklistOptions,
        },
        loggedInUser,
        fieldId,
        tenantId,
        tasksEditMode,
        stopForSelectedAsset,
      };

      try {
        taskFormActions.isTaskTypeParent(false);

        await this.updateManifestFormData(originalTask.id, false);
        const taskRes = await taskViewActions.updateTask(values, schema, taskParams);
        return taskRes;
      } catch (e) {
        logUtils.logError(e, 'Updating Task API Failed');
        throw e;
      } finally {
        this.setState({
          buttonDisableState: false,
        });
      }
    } else {
      this.setState({
        buttonDisableState: false,
      });
      formik.setFieldValue('s_status', this.props.originalTask.status);
    }
    // eslint-disable-next-line consistent-return
    return null;
  };

  onApproveButtonClicked = async (values, formik) => {
    const isTaskFormValid = await this.validateOverallTaskForm(formik, {}, values);
    if (isTaskFormValid) {
      const {
        taskViewActions,
        schema,
        originalTask,
        loggedInUser,
        assigneeOptions,
        taskTypeOptions,
        assetOptions,
        stopOptions,
        fieldId,
        tenantId,
        tasksEditMode,
        history,
        stopForSelectedAsset,
        customPicklistOptions,
      } = this.props;
      if (originalTask.isVirtual) {
        this.onCreateButtonClicked(values, formik);
        return;
      }
      try {
        await taskViewActions.approveTask(values, schema, {
          originalTask,
          picklistOptions: {
            assigneeOptions,
            taskTypeOptions,
            stopOptions,
            assetOptions,
            customPicklistOptions,
          },
          loggedInUser,
          fieldId,
          tenantId,
          tasksEditMode,
          stopForSelectedAsset,
        });
        history.goBack();
      } catch (e) {
        logUtils.logError(e, 'Updating Task API Failed');
      }
    }
  };

  setIsPageLoading = (isLoading) => {
    this.setState({ isPageLoading: isLoading });
  };

  onLinkTaskButtonClicked = () => {
    const { history, originalTask } = this.props;
    const { id: parentLinkedTaskId } = originalTask;
    if (parentLinkedTaskId === null) {
      console.error('parentLinkedTaskId is missing');
      return;
    }
    history.push(`/taskViewPage/task/new?parentLinkedTaskId=${parentLinkedTaskId}`);
  };

  editLinkedTask = (taskId) => {
    const { history, linkedTasks, taskViewActions } = this.props;
    const linkedTask = linkedTasks.items.find((t) => t.id === taskId);
    if (!linkedTask) {
      console.error(`did not find linked task ${taskId}`);
      return;
    }
    taskViewActions.pushTaskToState(linkedTask);
    history.push(`/taskViewPage/task/update/${taskId}`);
  };

  onDeleteButtonClicked = (task) => {
    this.confirmDeleteModal.current.showModal({
      onConfirm: () => {
        this.deleteLinkedTask(task.id);
        this.closeConfirmDeleteModal();
      },
    });
  };

  closeConfirmDeleteModal = () => {
    this.confirmDeleteModal.current.hideModal();
  };

  deleteLinkedTask = (taskId) => {
    const { taskFormActions } = this.props;
    taskFormActions.deleteLinkedTask(taskId);
  };

  onSearchStop = ({ event, formik }) => {
    if (_.size(_.get(event, 'target.value', '')) > 2) {
      const { taskTypeOptions, taskFormActions } = this.props;
      const selectedTaskTypeId = formik.values.s_tasktype;
      const selectedTaskType = taskTypeOptions.items.find(
        (option) => option.value === selectedTaskTypeId
      ).originalobject;
      if (!this.debouncedFn) {
        this.debouncedFn = _.debounce(async (searchString, taskType, loadStopPicklistFn) => {
          try {
            console.log('taskType', taskType?.name);
            loadStopPicklistFn({ taskType, searchString });
          } catch (error) {
            console.error(error);
          }
        }, 500);
      }
      this.debouncedFn(_.get(event, 'target.value', ''), selectedTaskType, taskFormActions.loadStopPicklist);
    }
  };

  onSearchAsset = ({ event, formik }) => {
    if (_.size(_.get(event, 'target.value', '')) > 2) {
      const { taskTypeOptions, taskFormActions } = this.props;
      const selectedTaskTypeId = formik.values.s_tasktype;
      const selectedStopId = formik.values.s_stop;
      const selectedTaskType = taskTypeOptions.items.find(
        (option) => option.value === selectedTaskTypeId
      ).originalobject;
      if (!this.assetDebouncedFn) {
        this.assetDebouncedFn = _.debounce(async (searchString, taskType, stopId, loadAssetPicklistFn) => {
          try {
            console.log('taskType', taskType?.name);
            loadAssetPicklistFn({
              taskType,
              searchString,
              stopId,
              filterAssetLevels: taskType.allowAssetLevelsInfo,
            });
          } catch (error) {
            console.error(error);
          }
        }, 500);
      }
      this.assetDebouncedFn(
        _.get(event, 'target.value', ''),
        selectedTaskType,
        selectedStopId,
        taskFormActions.loadAssetPicklist
      );
    }
  };

  onParentTempalteItemChange = (data, item) => {
    const { value } = data;
    const { taskFormActions, taskTypeOptions } = this.props;
    const { workflowStepId, taskTypeId } = item.originalItem;

    const stepTaskType = taskTypeOptions.items.find((option) => option.value === taskTypeId).originalobject;

    let stopId = null;
    let asset = null;

    if (value) {
      asset = item.assetOptions.items.find((option) => option.value === value).originalobject;
      if (asset && asset.type !== 'stop') {
        stopId = asset.stopId;
      }
    }

    taskFormActions.updateParentTaskTemplateItemOnAssetChange({
      assetId: value,
      workflowStepId,
      asset,
      taskType: stepTaskType,
      stopId,
      assetOptionsItems: item.assetOptions.items,
    });
  };

  onParentTempalteSearchChange = (event, item, taskTypeId) => {
    event.persist();
    const { value } = event.target;
    const { taskFormActions } = this.props;
    if (taskTypeId && value && value.length > 2) {
      const { workflowStepId } = item.originalItem;
      const searchString = event.target.value;

      if (
        !this.parentTemplateItemdebouncedFn ||
        (this.parentTemplateItemdebouncedFnId && this.parentTemplateItemdebouncedFnId !== workflowStepId)
      ) {
        this.parentTemplateItemdebouncedFn = _.debounce((values) => {
          taskFormActions.loadParentTaskTemplateItemAssests({
            taskTypeId: values.taskTypeId,
            searchString: values.searchString,
            workflowStepId: values.workflowStepId,
          });
        }, 500);
        this.parentTemplateItemdebouncedFnId = workflowStepId;
      }
      this.parentTemplateItemdebouncedFn({
        taskTypeId,
        searchString,
        workflowStepId,
      });
    }
  };

  onTemplateItemTaskTypeChanged = async (data, item, formik) => {
    const { taskFormActions, assetOptions, taskTypeOptions, stopOptions } = this.props;

    let assetId = formik.values.s_asset;
    if (_.isEmpty(assetId)) {
      assetId = formik.values.s_stop;
    }
    if (_.isEmpty(assetId)) {
      assetId = null;
    }

    taskFormActions.updateParentTaskTemplateItemOnKeyChange({
      keyValue: { ...item.originalItem, taskTypeId: data.value },
      key: 'originalItem',
      workflowStepId: item.originalItem.workflowStepId,
    });

    if (!data.value) {
      taskFormActions.removeItemFromParentTaskTemplate(item.originalItem.workflowStepId);
    } else if (assetId) {
      const asset = [..._.get(assetOptions, 'items', []), ..._.get(stopOptions, 'items', [])].find(
        (option) => option.value === assetId
      ).originalobject;
      const selectedTaskType = taskTypeOptions.items.find((option) => option.value === data.value).originalobject;
      if (selectedTaskType && asset) {
        try {
          const assetResponse = await apiService.getAssetsByTeamIdsAndAreaIds(
            [selectedTaskType.teamId],
            (asset.areas || []).filter((area) => !area.isDleted).map((area) => area.areaId),
            undefined,
            undefined,
            undefined,
            undefined,
            assetId
          );
          let assets = inflateResponse(assetResponse);
          assets = (assets || []).filter((i) => !i.isDeleted);
          if (assets.length) {
            taskFormActions.loadPreferredLocationsAndAssigneeForParentTaskTemplateItem([
              {
                assetId,
                taskTypeId: data.value,
                workflowStepId: item.originalItem.workflowStepId,
                assigneeIdsToEvaluate: [],
                equipmentIdToEvaluate: '',
              },
            ]);
          }
        } catch (error) {
          console.error(error, error.message);
        }
      }
    }
  };

  onAssetCriteriaChange = (data, formik) => {
    const { name, value } = data;
    const { taskFormActions, assetCriteriaTemplate, fieldId, taskTypeOptions = [] } = this.props;
    const { info = {} } = assetCriteriaTemplate;
    let areaIdsSelected = [];

    const newInfo = {
      ...info,
      [name]: value,
      previewInfo: {
        value: '',
        isLoading: false,
      },
    };

    const allTypesOptions = JSON.parse(JSON.stringify(Object.values(ASSET_TYPES)));

    const impactedValuesObj = {};

    if (name !== 'locationIds') {
      impactedValuesObj.locationSearchInfo = {
        isLoading: false,
        items: [],
        selectedItems: [],
      };
      impactedValuesObj.locationIds = [];
    }

    if (name === 'areaIds' || name === 'locationIds') {
      // dropdowns reset
      impactedValuesObj.stopIds = [];
      impactedValuesObj.types = [];
      impactedValuesObj.typesOptions = allTypesOptions;
      impactedValuesObj.customAttributes = {};

      impactedValuesObj.stopSearchInfo = {
        isLoading: false,
        items: [],
        selectedItems: [],
      };
      if (!value.length) {
        impactedValuesObj.areaIds = info.areasOptions.map((item) => item.key);
      }
    }

    if (name === 'stopIds') {
      if (newInfo.stopIds?.length) {
        // only object types needs to be shown
        impactedValuesObj.types = (newInfo.types || []).filter((key) => key !== ASSET_TYPES.STOP.key);
        impactedValuesObj.typesOptions = allTypesOptions.filter((item) => item.key !== ASSET_TYPES.STOP.key);
      } else {
        impactedValuesObj.types = [];
        impactedValuesObj.typesOptions = allTypesOptions;
      }
    }

    if (name === 'stopIds' || name === 'locationIds') {
      const key = name === 'stopIds' ? 'stopSearchInfo' : 'locationSearchInfo';
      const newSelecedItems = (newInfo?.[key]?.items || []).filter((item) => value.indexOf(item.key) !== -1);
      impactedValuesObj[key] = {
        ...(newInfo?.[key] || {}),
        ...{ selectedItems: newSelecedItems },
      };
    }
    const finalInfo = { ...newInfo, ...impactedValuesObj };

    const inputToSave = getAssetCriteriaRequestObj(finalInfo);

    formik.setFieldValue('s_assetCriteria', inputToSave, false);

    taskFormActions.updateAssetCriteriaTemplateInfo(finalInfo);

    const selectedTaskTypeId = formik.values.s_tasktype;
    if (selectedTaskTypeId) {
      const selectedTaskType = taskTypeOptions.items.find(
        (option) => option.value === selectedTaskTypeId
      ).originalobject;
      areaIdsSelected = finalInfo.areaIds;
      if (selectedTaskType && finalInfo?.locationSearchInfo?.selectedItems?.length) {
        areaIdsSelected = _.flatMap(finalInfo.locationSearchInfo.selectedItems, (item) => {
          const { originalobject = {} } = item;
          const { areas = [] } = originalobject;
          return (areas || []).filter((a) => !a.isDeleted).map((a) => a.areaId);
        });
      }
      taskFormActions.loadAssigneePicklist(null, selectedTaskType, fieldId, _.uniq(areaIdsSelected));
    }
  };

  onAssetCriteriaStopSearchChange = ({ formik, event, dropdownFor }) => {
    event.persist();
    const { taskFormActions, taskTypeOptions } = this.props;

    const selectedTaskTypeId = formik.values.s_tasktype;
    const selectedTaskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId).originalobject;
    const searchString = event.target.value;

    if (!this.stopAssetCriteriadebouncedFn) {
      this.stopAssetCriteriadebouncedFn = _.debounce((values) => {
        taskFormActions.searchAssetsForAssetCriteria({
          taskType: values.taskType,
          searchString: values.searchString,
          dropdownFor: values.dropdownFor,
        });
      }, 500);
    }
    this.stopAssetCriteriadebouncedFn({ taskType: selectedTaskType, searchString, dropdownFor });
  };

  onAssetCriteriaLocationSearchChange = ({ formik, event, dropdownFor }) => {
    event.persist();
    const { taskFormActions, taskTypeOptions } = this.props;

    const selectedTaskTypeId = formik.values.s_tasktype;
    const selectedTaskType = taskTypeOptions.items.find((option) => option.value === selectedTaskTypeId).originalobject;
    const searchString = event.target.value;

    if (!this.locationAssetCriteriadebouncedFn) {
      this.locationAssetCriteriadebouncedFn = _.debounce((values) => {
        taskFormActions.searchAssetsForAssetCriteria({
          taskType: values.taskType,
          searchString: values.searchString,
          dropdownFor: values.dropdownFor,
        });
      }, 500);
    }
    this.locationAssetCriteriadebouncedFn({ taskType: selectedTaskType, searchString, dropdownFor });
  };

  onAssetCriteriaPreviewButtonClicked = () => {
    const { taskFormActions } = this.props;

    taskFormActions.getAssetsForAssetCriteria();
  };

  handleClickAssignee = (assigneeId) => {
    const { resetViewInNavigationComponent } = this.props;
    resetViewInNavigationComponent(CONTAINERS.TASK_VIEW, {
      component: UserCard,
      props: {
        assigneeId,
      },
    });
  };

  render() {
    const {
      taskTypeOptions,
      stopOptions,
      assigneeOptions,
      assetOptions,
      customPicklistOptions,
      schema,
      formValues,
      originalTask,
      loggedInUser,
      dependentTasks,
      linkedTasks,
      templateInfo,
      parentTaskTemplate,
      notificationWatchers,
      stopForSelectedAsset,
      partsPicklist,
      manifestFormPreview,
      picklistMappings,
      allPicklistData,
      allAssetAttributes,
      allAreas,
      taskHistoryList,
      taskEmailHistory,
      watchergroups,
      taskTypeSelectedisParent,
      showEquipmentColumnForParentTemplate = false,
      vendorsDict = {},
      bulkCreateTasksView,
      settings,
      assetLevelOptions,
      assetLevelsDict,
    } = this.props;
    const { isPageLoading, confirmModal } = this.state;
    const taskHistory = taskHistoryList.map((taskHistorylist) => taskHistoryMapper(taskHistorylist));
    let gridWidth = originalTask.id && !isTaskParent(originalTask) ? 11 : 16;
    if (this.ifSmallScreen) {
      gridWidth = 16;
    }
    return (
      <>
        {isPageLoading && <Joyn2Loader />}
        <Container fluid id="task-form" className="container-above-task-view-grid">
          <FormHeader
            // eslint-disable-next-line no-nested-ternary
            title={`${originalTask.id ? 'Update Task' : bulkCreateTasksView ? 'Create Bulk Tasks' : 'Create New Task'}`}
            onCancel={this.cancelTask}
          />

          <Grid style={{ height: 'calc(100% - 48px' }} padded>
            <Grid.Row style={{ height: '100%' }}>
              <Grid.Column style={{ height: '100%' }} width={gridWidth}>
                <TaskForm
                  ref={{
                    taskForm: this.taskFormRef,
                    manifestForm: this.manifestForm,
                  }}
                  allTasks={this.props.allTasks}
                  history={this.props.history}
                  onApprove={this.onApproveButtonClicked}
                  onCancel={this.cancelTask}
                  onUpdate={this.onUpdateButtonClicked}
                  buttonDisableState={this.state.buttonDisableState}
                  task={originalTask}
                  taskHistory={taskHistory}
                  schema={schema}
                  initialValues={formValues}
                  validateForm={this.validateForm}
                  assigneeOptions={assigneeOptions}
                  assetOptions={assetOptions}
                  statusOptions={this.statusOptions}
                  stopOptions={stopOptions}
                  taskTypeOptions={taskTypeOptions}
                  eventSourceOptions={this.eventSourceOptions}
                  customPicklistOptions={customPicklistOptions}
                  onCreateButtonClicked={this.onCreateButtonClicked}
                  dependentTasks={dependentTasks}
                  taskTypeSelectedisParent={taskTypeSelectedisParent}
                  loggedInUser={loggedInUser}
                  linkedTasks={linkedTasks}
                  templateInfo={templateInfo}
                  parentTaskTemplate={parentTaskTemplate}
                  showEquipmentColumnForParentTemplate={showEquipmentColumnForParentTemplate}
                  onParentTempalteSearchChange={this.onParentTempalteSearchChange}
                  onParentTempalteItemChange={this.onParentTempalteItemChange}
                  onTemplateItemTaskTypeChanged={this.onTemplateItemTaskTypeChanged}
                  onLinkTaskButtonClicked={this.onLinkTaskButtonClicked}
                  editLinkedTask={this.editLinkedTask}
                  onValueChange={{
                    onTaskTypeChange: this.onTaskTypeChange,
                    onStopChange: this.onStopChange,
                    onAssetChange: this.onAssetChange,
                    onCustomPicklistValueChange: this.onCustomPicklistValueChange,
                    [ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS]: this[ON_TASK_TYPE_CHANGE_FOR_LINKED_TASKS],
                    onGlobalAttributeValueChange: this.onGlobalAttributeValueChange,
                    onScoreOrIncrementChange: this.onScoreOrIncrementChange,
                    viewAssigneeDetals: this.handleClickAssignee,
                    onAssigneeChange: this.onAssigneeChange,
                    onLevel1Change: this.onLevel1Change,
                    onLevel2Change: this.onLevel2Change,
                  }}
                  onDeleteButtonClicked={this.onDeleteButtonClicked}
                  notificationWatchers={notificationWatchers}
                  onSearchStop={this.onSearchStop}
                  onSearchAsset={this.onSearchAsset}
                  stopForSelectedAsset={stopForSelectedAsset}
                  partsPicklist={partsPicklist}
                  manifestFormPreview={manifestFormPreview}
                  picklistMappings={picklistMappings}
                  allPicklistData={allPicklistData}
                  allAreas={allAreas}
                  validateOverallTaskForm={this.validateOverallTaskForm}
                  taskEmailHistory={taskEmailHistory}
                  watchergroups={watchergroups}
                  onResolveAndUpdateButtonClicked={this.onResolveAndUpdateButtonClicked}
                  vendorsDict={vendorsDict}
                  bulkCreateTasksView={this.props.bulkCreateTasksView}
                  assetCriteriaTemplate={this.props.assetCriteriaTemplate}
                  onAssetCriteriaChange={this.onAssetCriteriaChange}
                  onAssetCriteriaStopSearchChange={this.onAssetCriteriaStopSearchChange}
                  onAssetCriteriaLocationSearchChange={this.onAssetCriteriaLocationSearchChange}
                  onAssetCriteriaPreviewButtonClicked={this.onAssetCriteriaPreviewButtonClicked}
                  allAssetAttributes={allAssetAttributes}
                  evaluatedRules={this.props.evaluatedRules}
                  settings={settings}
                  assetLevelOptions={assetLevelOptions}
                  assetLevelsDict={assetLevelsDict}
                  ifSmallScreen={this.ifSmallScreen}
                />
              </Grid.Column>
              {originalTask.id && !isTaskParent(originalTask) && !this.ifSmallScreen && (
                <Grid.Column width={5} style={{ height: '100%' }}>
                  <Comments task={originalTask} />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
          <Modal
            ref={this.confirmDeleteModal}
            render={(props) => (
              <Confirm
                size="tiny"
                header="Warning"
                content="Deleting linked task cannot be undone. Are you sure you would like to continue?"
                onCancel={this.closeConfirmDeleteModal}
                open
                {...props}
              />
            )}
          />

          <CardNavigationComponent container={CONTAINERS.TASK_VIEW} />
          {confirmModal.open && <Confirm size="mini" {...confirmModal} />}
        </Container>
      </>
    );
  }
}

function mapStateToProps(state) {
  const {
    manageTaskFormReducer: {
      taskHistoryState: { items: taskHistoryItems },
      taskEmailHistory = [],
      evaluatedRules = [],
    },
    vendorReducer: { vendors },
    watcherGroupReducer: { watchergroups },
    appReducer: { assetLevelsDict = {} },
  } = state;

  return {
    requestPageView: state.approvalTasksReducer.view,
    originalTask: state.manageTaskFormReducer.task,
    taskTypeOptions: state.manageTaskFormReducer[PICKLIST_OPTIONS.TASK_TYPE],
    stopOptions: state.manageTaskFormReducer[PICKLIST_OPTIONS.STOP],
    assigneeOptions: state.manageTaskFormReducer[PICKLIST_OPTIONS.ASSIGNEE],
    assetOptions: state.manageTaskFormReducer[PICKLIST_OPTIONS.ASSET],
    customPicklistOptions: state.manageTaskFormReducer.customPicklistOptions,
    schema: state.manageTaskFormReducer.schema,
    formValues: state.manageTaskFormReducer.formValues,
    taskTypeSelectedisParent: state.manageTaskFormReducer.taskTypeSelectedisParent,
    allTasks: state.taskViewPageReducer.tasks.items,
    tenantId: state.appReducer.tenantId,
    loggedInUser: state.appReducer.loggedInUser,
    fieldId: state.appReducer.selectedField.id,
    dependentTasks: state.manageTaskFormReducer.dependentTasks,
    linkedTasks: state.manageTaskFormReducer.linkedTasks,
    templateInfo: state.manageTaskFormReducer.templateInfo,
    parentTaskTemplate: state.manageTaskFormReducer.parentTaskTemplate,
    assetCriteriaTemplate: state.manageTaskFormReducer.assetCriteriaTemplate,
    childTasksDictByWorkflowId: state.manageTaskFormReducer.childTasksDictByWorkflowId,
    showEquipmentColumnForParentTemplate: state.manageTaskFormReducer.showEquipmentColumnForParentTemplate,
    parentTaskTemplateTaskTypesDict: state.manageTaskFormReducer.parentTaskTemplateTaskTypesDict,
    tasksEditMode: state.taskViewPageReducer.tasksEditMode,
    parentLinkedTask: state.manageTaskFormReducer.parentLinkedTask,
    notificationWatchers: state.manageTaskFormReducer.notificationWatchers,
    stopForSelectedAsset: state.manageTaskFormReducer.stopForSelectedAsset,
    partsPicklist: state.manageTaskFormReducer.partsPicklist,
    manifestFormPreview: state.manageTaskFormReducer.manifestForm,
    approvalRequests: state.approvalTasksReducer.approvalRequests,
    workRequests: state.approvalTasksReducer.workRequests,
    isDRScoreEnabled: getIsDRScoreEnabled(state),
    isAdhocFileUploading: isAdhocFileUploadingSelector(state),
    picklistMappings: state.picklistMappingReducer.picklistMappings,
    allPicklistData: state.picklistReducer.picklists,
    allAssetAttributes: state.picklistReducer.assetAttributes,
    allAreas: state.appReducer.allAreas,
    taskHistoryList: taskHistoryItems,
    taskEmailHistory,
    watchergroups,
    vendorsDict: _.keyBy(vendors, 'id'),
    evaluatedRules,
    assetLevelOptions: state.manageTaskFormReducer[PICKLIST_OPTIONS.ASSET_LEVELS_OPTIONS],
    settings: state.appReducer.settings,
    assetLevelsDict,
    selectedTaskTypeGroups: state.appReducer.selectedTaskTypeGroups,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    appActions: bindActionCreators(appActions, dispatch),
    taskViewActions: bindActionCreators(allTaskViewActions, dispatch),
    taskFormActions: bindActionCreators(manageTaskFormActions, dispatch),
    approvalActions: bindActionCreators(manageApprovalActions, dispatch),
    resetViewInNavigationComponent: bindActionCreators(resetView, dispatch),
    closeViewInNavigationComponent: bindActionCreators(closeView, dispatch),
    loadPicklistMappingList: bindActionCreators(loadAllPicklistMappings, dispatch),
    loadWatcherGroupsForField: bindActionCreators(loadWatcherGroups, dispatch),
    loadAllPicklists: bindActionCreators(loadPicklists, dispatch),
    loadAssetAttributes: bindActionCreators(downloadAllAssetAttributes, dispatch),
  };
}

ManageTaskForm.propTypes = {
  appActions: PropTypes.object.isRequired,
  requestPageView: PropTypes.string,
  task: PropTypes.object,
  allTasks: PropTypes.array.isRequired,
  tenantId: PropTypes.string.isRequired,
  tasksEditMode: PropTypes.string.isRequired,
  taskFormActions: PropTypes.object.isRequired,
  taskTypeSelectedisParent: PropTypes.bool.isRequired,
  taskViewActions: PropTypes.object.isRequired,
  approvalActions: PropTypes.object.isRequired,
  formValues: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  fieldId: PropTypes.string.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  originalTask: PropTypes.object.isRequired,
  taskTypeOptions: PropTypes.object.isRequired,
  stopOptions: PropTypes.object.isRequired,
  assigneeOptions: PropTypes.object.isRequired,
  assetOptions: PropTypes.object.isRequired,
  customPicklistOptions: PropTypes.object.isRequired,
  dependentTasks: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  linkedTasks: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  templateInfo: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  parentTaskTemplate: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  assetCriteriaTemplate: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    info: PropTypes.object,
  }).isRequired,
  showEquipmentColumnForParentTemplate: PropTypes.bool,
  parentTaskTemplateTaskTypesDict: PropTypes.object,
  childTasksDictByWorkflowId: PropTypes.object,
  parentLinkedTask: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  notificationWatchers: PropTypes.object,
  stopForSelectedAsset: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  partsPicklist: PropTypes.object,
  manifestFormPreview: PropTypes.object,
  workRequests: PropTypes.object,
  approvalRequests: PropTypes.object,
  resetViewInNavigationComponent: PropTypes.func,
  closeViewInNavigationComponent: PropTypes.func,
  isDRScoreEnabled: PropTypes.bool.isRequired,
  isAdhocFileUploading: PropTypes.bool.isRequired,
  loadPicklistMappingList: PropTypes.func,
  picklistMappings: PropTypes.array,
  allPicklistData: PropTypes.array,
  allAssetAttributes: PropTypes.array,
  allAreas: PropTypes.array,
  taskHistoryList: PropTypes.array,
  taskEmailHistory: PropTypes.array,
  loadWatcherGroupsForField: PropTypes.func,
  watchergroups: PropTypes.array,
  loadAllPicklists: PropTypes.func,
  vendorsDict: PropTypes.object,
  loadAssetAttributes: PropTypes.func,
  bulkCreateTasksView: PropTypes.bool,
  evaluatedRules: PropTypes.array,
  assetLevelOptions: PropTypes.object,
  settings: PropTypes.object,
  assetLevelsDict: PropTypes.object,
  selectedTaskTypeGroups: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageTaskForm);
